import { useMutation, useQuery } from "@tanstack/react-query";
import apiServices from "../ApiServices/request-handler";
import { SwalAlert } from "../components";
import Analytics from "../firebase_services/analytics";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../context/authContext";

export const useLoginMutation = (
  login,
  dispatch,
  setLoading,
  location,
  navigate
) => {
  return useMutation({
    mutationFn: async (loginUser) => {
      const response = await apiServices.login(loginUser);
      return response.data;
    },
    onSuccess: async (data) => {
      login(data?.access_token, data?.user?.id);
      await Analytics.trackLogin(data?.user?.email, data?.role);
      dispatch({
        type: "UPDATE_USER_DATA",
        payload: { userData: data },
      });
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
      setLoading(false);
      const redirectTo =
        new URLSearchParams(location.search).get("redirect") || "/";
      navigate(redirectTo); // adjust this to your desired redirect path
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      if (error.response?.data?.message === "User is not verified.") {
        dispatch({
          type: "UPDATE_EMAIL_VERIFIED_DATA",
          payload: { emailVerified: error.response?.data?.user?.email },
        });
        navigate(`/reVerified-otp/${error.response?.data?.user?.id}`);
        dispatch({
          type: "UPDATE_ROUTE_DATA",
          payload: { routeData: "/login" },
        });
      }
      setLoading(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const UseRegistermutation = (
  setLoadingToapi,
  navigate,
  keepPrivate,
  getValues,
  dispatch,
  paramRef,
  paramEmail,
  setApiMessage  // NEW: Add this parameter
) => {
  return useMutation({
    mutationFn: async (registerData) => {
      const params = {};
      if (paramRef) {
        params.paramRef = paramRef;
      }
      if (paramEmail) {
        params.paramEmail = paramEmail;
      }
      const response = await apiServices.register(registerData, params, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },
    onSuccess: async (data) => {
      await Analytics.trackRegister(data?.user?.email, data?.role);
      setLoadingToapi(false);
      
      // Check if user registered with phone and is under review
      if (data?.is_under_review) {
        // Show the API message instead of redirecting
        setApiMessage(data.message);
      } else {
        // Normal flow for email registration
        SwalAlert("Success!", data.message, "success");
        navigate(`/verified-otp/${data?.user?.id}`);
        const keepPrivateEmail = keepPrivate ? getValues("email") : "";
        dispatch({
          type: "UPDATE_EMAIL_DATA",
          payload: { email: keepPrivateEmail },
        });
        dispatch({
          type: "UPDATE_ACCESS_TOKEN",
          payload: { access_token: data.access_token },
        });
        dispatch({
          type: "UPDATE_EMAIL_VERIFIED_DATA",
          payload: { emailVerified: data?.user?.email },
        });
        dispatch({
          type: "UPDATE_ROUTE_DATA",
          payload: { routeData: "/" },
        });
      }
    },
    onError: (error) => {
      const responseData = error.response?.data?.message;
      const message = responseData
        ? Object.entries(responseData)
            .map(([key, value]) => `${value.join(", ")}`)
            .join("\n")
        : "An unexpected error occurred.";
      setLoadingToapi(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const useEmailVerifiedMutation = (
  setLoading,
  navigate,
  routeData,
  refetchUserData
) => {
  const dispatch = useDispatch();
  const access_token = useSelector(
    (state) => state.app.access_token.access_token
  );
  const { login } = useAuth();
  return useMutation({
    mutationFn: async (emailVerifiedData) => {
      const response = await apiServices.verifyOtp(emailVerifiedData);
      return response.data;
    },
    onSuccess: async (data) => {
      dispatch({
        type: "UPDATE_EMAIL_VERIFIED_DATA",
        payload: { emailVerified: data?.email },
      });

      if (refetchUserData) {
        const { data: refreshedUser } = await refetchUserData();
        await login(access_token, refreshedUser?.user?.id);
        // await Analytics.trackOtpVerified(refreshedUser?.role);
        dispatch({
          type: "UPDATE_USER_DATA",
          payload: { userData: refreshedUser },
        });
      }

      navigate(`${routeData}`);
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
      setLoading(false);
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      setLoading(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const useRequestVerifyOtpMutation = () => {
  return useMutation({
    mutationFn: async (RequestVerifyOtp) => {
      const response = await apiServices.requestVerifyOtp(RequestVerifyOtp);
      return response.data;
    },
    onSuccess: async (data) => {
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const useEmailVerifiedToPasswordMutation = (
  setLoading,
  navigate,
  dispatch
) => {
  return useMutation({
    mutationFn: async (emailVerifiedToPasswordData) => {
      const response = await apiServices.requestForgotPassword(
        emailVerifiedToPasswordData
      );
      return response.data;
    },
    onSuccess: async (data) => {
      dispatch({
        type: "UPDATE_EMAIL_VERIFIED_DATA",
        payload: { emailVerified: data?.email },
      });
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
      setLoading(false);
      navigate(`/verified-otp`);
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      setLoading(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const useResendOTPMutation = (dispatch) => {
  return useMutation({
    mutationFn: async (emailVerifiedToPasswordData) => {
      const response = await apiServices.requestForgotPassword(
        emailVerifiedToPasswordData
      );
      return response.data;
    },
    onSuccess: async (data) => {
      dispatch({
        type: "UPDATE_EMAIL_VERIFIED_DATA",
        payload: { emailVerified: data?.email },
      });
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      SwalAlert("Oops...", message, "error");
    },
  });
};

export const useResetPasswordMutation = (setLoading, navigate) => {
  return useMutation({
    mutationFn: async (resetPasswordData) => {
      const response = await apiServices.resetPassword(resetPasswordData);
      return response.data;
    },
    onSuccess: async (data) => {
      const title = data.status;
      const message = data.message;
      SwalAlert(title, message, "success");
      setLoading(false);
      navigate("/login");
    },
    onError: (error) => {
      const message = error.response?.data?.message;
      setLoading(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

const fetchTalentImageList = async () => {
  const response = await apiServices.talentHeroImages();
  return response?.data?.data;
};

export const useTalentImage = () => {
  return useQuery({
    queryKey: ["talentListImage"],
    queryFn: fetchTalentImageList,
  });
};

const fetchUserData = async ({ queryKey }) => {
  const [, userId] = queryKey;
  const response = await apiServices.userDetails(userId);
  return response?.data;
};
export const useUserData = (userId) => {
  return useQuery({
    queryKey: ["useUserData", userId],
    queryFn: fetchUserData,
    enabled: !userId,
  });
};

const fetchuserprofile = async ({ queryKey }) => {
  const [, userId] = queryKey;
  const response = await apiServices.users(userId);
  return response?.data;
};
export const useUser = (userId) => {
  return useQuery({
    queryKey: ["userprofile", userId],
    queryFn: fetchuserprofile,
    enabled: !!userId,
  });
};

export const useUpdateMutation = (
  userId,
  dispatch,
  setLoadingToapi,
  navigate
) => {
  return useMutation({
    mutationFn: async (userUpdateData) => {
      const response = await apiServices.userUpdate(userUpdateData, userId);
      return response.data;
    },
    onSuccess: async (data) => {
      await Analytics.trackUpdateProfile(data?.role, "update user profile");
      SwalAlert(data?.status, data?.message, "success");
      setLoadingToapi(false);
      dispatch({
        type: "UPDATE_USER_DATA",
        payload: { userData: data },
      });
      navigate("/dashboard");
    },
    onError: (error) => {
      const responseData = error.response?.data?.message;
      const message = responseData
        ? Object.entries(responseData)
            .map(([key, value]) => `${key}: ${value.join(", ")}`)
            .join("\n")
        : "An unexpected error occurred.";
      setLoadingToapi(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

const fetchdropdowns = async () => {
  const response = await apiServices.dropdowns();
  return response?.data;
};

export const useDropdown = () => {
  return useQuery({
    queryKey: ["dropdowns"],
    queryFn: fetchdropdowns,
  });
};

const fetchJobDropdowns = async () => {
  const response = await apiServices.jobDropdowns();
  return response?.data;
};

export const UsejobDropdowns = () => {
  return useQuery({
    queryKey: ["jobDropdowns"],
    queryFn: fetchJobDropdowns,
  });
};

export const useEmpResgiterMutation = (
  employerId,
  setLoadingToapi,
  navigate,
  dispatch,
  isLogin
) => {
  const { logout } = useAuth();
  return useMutation({
    mutationFn: async (EmployerData) => {
      const response = await apiServices.employersAuth(
        employerId,
        EmployerData
      );
      return response.data;
    },
    onSuccess: async (data) => {
      if (employerId) {
        await Analytics.trackUpdateProfile(
          data?.role,
          "update employer profile"
        );
      } else {
        await Analytics.trackUpgrade(data?.role);
      }
      dispatch({
        type: "UPDATE_USER_DATA",
        payload: { userData: data },
      });
      SwalAlert("Success!", data.message, "success");
      setLoadingToapi(false);

      if (isLogin) logout();
      navigate(isLogin ? "/" : "/dashboard");
    },
    onError: (error) => {
      const responseData = error.response?.data?.errors;
      const message = responseData
        ? Object.entries(responseData)
            .map(([key, value]) => `${key}: ${value.join(", ")}`)
            .join("\n")
        : "An unexpected error occurred.";
      setLoadingToapi(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

const fetchIndustries = async () => {
  const response = await apiServices.industriesDropdown(100);
  return response?.data?.data;
};

export const useIndustries = () => {
  return useQuery({
    queryKey: ["Iidustries"],
    queryFn: fetchIndustries,
  });
};

const fetchStates = async ({ queryKey }) => {
  const [, selectedCountry] = queryKey;
  const response = await apiServices.states(selectedCountry);
  return response?.data;
};

export const useStatesDropdowns = (selectedCountry) => {
  return useQuery({
    queryKey: ["states", selectedCountry],
    queryFn: fetchStates,
    enabled: !!selectedCountry,
  });
};

const fetchcities = async ({ queryKey }) => {
  const [, selectedStates] = queryKey;
  const response = await apiServices.cities(selectedStates);
  return response?.data;
};

export const UsecitiesDropdowns = (selectedStates) => {
  return useQuery({
    queryKey: ["cities", selectedStates],
    queryFn: fetchcities,
    enabled: !!selectedStates,
  });
};

export const useTalentMutation = (
  talentId,
  dispatch,
  setLoadingToapi,
  navigate,
  isLogin
) => {
  const { logout } = useAuth();
  return useMutation({
    mutationFn: async (talentData) => {
      const response = await apiServices.talentAuth(talentId, talentData);
      return response.data;
    },
    onSuccess: async (data) => {
      if (talentId) {
        await Analytics.trackUpdateProfile(data?.role, "update talent profile");
      } else {
        await Analytics.trackUpgrade(data?.role);
      }
      dispatch({
        type: "UPDATE_USER_DATA",
        payload: { userData: data },
      });
      SwalAlert("Success!", data.message, "success");
      setLoadingToapi(false);

      if (isLogin) logout();
      navigate(isLogin ? "/" : "/dashboard");
    },
    onError: (error) => {
      const responseData = error.response?.data?.errors;
      const message = responseData
        ? Object.entries(responseData)
            .map(([key, value]) => `${value.join(", ")}`)
            .join("\n")
        : "An unexpected error occurred.";
      setLoadingToapi(false);
      SwalAlert("Oops...", message, "error");
    },
  });
};

const fetchTalentList = async ({ queryKey }) => {
  const [, talent_id] = queryKey;
  const response = await apiServices.talentDetails(talent_id);
  return response?.data?.data;
};

export const useUpdateTalent = (talent_id) => {
  return useQuery({
    queryKey: ["updateAccountTalent", talent_id],
    queryFn: fetchTalentList,
  });
};
